import React from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Tooltip } from '@mui/material';
import KitTooltip from '../ui/Tooltip';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import PendingIcon from '@mui/icons-material/Pending';
import RemoveIcon from '@mui/icons-material/Remove';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// Every assigned ISO 3166-1 alpha-3 code, so any country a security or a
// country picker shows can render its flag.
export const COUNTRY_ISO3_TO_ISO2 = {
    ABW: 'AW', AFG: 'AF', AGO: 'AO', AIA: 'AI', ALA: 'AX', ALB: 'AL', AND: 'AD', ARE: 'AE',
    ARG: 'AR', ARM: 'AM', ASM: 'AS', ATA: 'AQ', ATF: 'TF', ATG: 'AG', AUS: 'AU', AUT: 'AT',
    AZE: 'AZ',
    BDI: 'BI', BEL: 'BE', BEN: 'BJ', BES: 'BQ', BFA: 'BF', BGD: 'BD', BGR: 'BG', BHR: 'BH',
    BHS: 'BS', BIH: 'BA', BLM: 'BL', BLR: 'BY', BLZ: 'BZ', BMU: 'BM', BOL: 'BO', BRA: 'BR',
    BRB: 'BB', BRN: 'BN', BTN: 'BT', BVT: 'BV', BWA: 'BW',
    CAF: 'CF', CAN: 'CA', CCK: 'CC', CHE: 'CH', CHL: 'CL', CHN: 'CN', CIV: 'CI', CMR: 'CM',
    COD: 'CD', COG: 'CG', COK: 'CK', COL: 'CO', COM: 'KM', CPV: 'CV', CRI: 'CR', CUB: 'CU',
    CUW: 'CW', CXR: 'CX', CYM: 'KY', CYP: 'CY', CZE: 'CZ',
    DEU: 'DE', DJI: 'DJ', DMA: 'DM', DNK: 'DK', DOM: 'DO', DZA: 'DZ',
    ECU: 'EC', EGY: 'EG', ERI: 'ER', ESH: 'EH', ESP: 'ES', EST: 'EE', ETH: 'ET',
    FIN: 'FI', FJI: 'FJ', FLK: 'FK', FRA: 'FR', FRO: 'FO', FSM: 'FM',
    GAB: 'GA', GBR: 'GB', GEO: 'GE', GGY: 'GG', GHA: 'GH', GIB: 'GI', GIN: 'GN', GLP: 'GP',
    GMB: 'GM', GNB: 'GW', GNQ: 'GQ', GRC: 'GR', GRD: 'GD', GRL: 'GL', GTM: 'GT', GUF: 'GF',
    GUM: 'GU', GUY: 'GY',
    HKG: 'HK', HMD: 'HM', HND: 'HN', HRV: 'HR', HTI: 'HT', HUN: 'HU',
    IDN: 'ID', IMN: 'IM', IND: 'IN', IOT: 'IO', IRL: 'IE', IRN: 'IR', IRQ: 'IQ', ISL: 'IS',
    ISR: 'IL', ITA: 'IT',
    JAM: 'JM', JEY: 'JE', JOR: 'JO', JPN: 'JP',
    KAZ: 'KZ', KEN: 'KE', KGZ: 'KG', KHM: 'KH', KIR: 'KI', KNA: 'KN', KOR: 'KR', KWT: 'KW',
    LAO: 'LA', LBN: 'LB', LBR: 'LR', LBY: 'LY', LCA: 'LC', LIE: 'LI', LKA: 'LK', LSO: 'LS',
    LTU: 'LT', LUX: 'LU', LVA: 'LV',
    MAC: 'MO', MAF: 'MF', MAR: 'MA', MCO: 'MC', MDA: 'MD', MDG: 'MG', MDV: 'MV', MEX: 'MX',
    MHL: 'MH', MKD: 'MK', MLI: 'ML', MLT: 'MT', MMR: 'MM', MNE: 'ME', MNG: 'MN', MNP: 'MP',
    MOZ: 'MZ', MRT: 'MR', MSR: 'MS', MTQ: 'MQ', MUS: 'MU', MWI: 'MW', MYS: 'MY', MYT: 'YT',
    NAM: 'NA', NCL: 'NC', NER: 'NE', NFK: 'NF', NGA: 'NG', NIC: 'NI', NIU: 'NU', NLD: 'NL',
    NOR: 'NO', NPL: 'NP', NRU: 'NR', NZL: 'NZ',
    OMN: 'OM',
    PAK: 'PK', PAN: 'PA', PCN: 'PN', PER: 'PE', PHL: 'PH', PLW: 'PW', PNG: 'PG', POL: 'PL',
    PRI: 'PR', PRK: 'KP', PRT: 'PT', PRY: 'PY', PSE: 'PS', PYF: 'PF',
    QAT: 'QA',
    REU: 'RE', ROU: 'RO', RUS: 'RU', RWA: 'RW',
    SAU: 'SA', SDN: 'SD', SEN: 'SN', SGP: 'SG', SGS: 'GS', SHN: 'SH', SJM: 'SJ', SLB: 'SB',
    SLE: 'SL', SLV: 'SV', SMR: 'SM', SOM: 'SO', SPM: 'PM', SRB: 'RS', SSD: 'SS', STP: 'ST',
    SUR: 'SR', SVK: 'SK', SVN: 'SI', SWE: 'SE', SWZ: 'SZ', SXM: 'SX', SYC: 'SC', SYR: 'SY',
    TCA: 'TC', TCD: 'TD', TGO: 'TG', THA: 'TH', TJK: 'TJ', TKL: 'TK', TKM: 'TM', TLS: 'TL',
    TON: 'TO', TTO: 'TT', TUN: 'TN', TUR: 'TR', TUV: 'TV', TWN: 'TW', TZA: 'TZ',
    UGA: 'UG', UKR: 'UA', UMI: 'UM', URY: 'UY', USA: 'US', UZB: 'UZ',
    VAT: 'VA', VCT: 'VC', VEN: 'VE', VGB: 'VG', VIR: 'VI', VNM: 'VN', VUT: 'VU',
    WLF: 'WF', WSM: 'WS',
    YEM: 'YE',
    ZAF: 'ZA', ZMB: 'ZM', ZWE: 'ZW',
};

const COUNTRY_DISPLAY_NAMES = typeof Intl !== 'undefined' && Intl.DisplayNames
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

export function normalizeCountryCode(value) {
    return String(value || '').trim().toUpperCase();
}

export function getCountryAvailabilityKeys(value) {
    const normalized = normalizeCountryCode(value);
    if (!normalized) {
        return [];
    }
    const iso2 = COUNTRY_ISO3_TO_ISO2[normalized]
        || (/^[A-Z]{2}$/.test(normalized) ? normalized : '');
    return [normalized, iso2].filter(Boolean);
}

export function countryCodeToFlag(iso2) {
    if (!/^[A-Z]{2}$/.test(iso2)) {
        return '';
    }
    return String.fromCodePoint(
        ...iso2.split('').map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65),
    );
}

export function getCountryChipDisplay(rawCountry) {
    const label = normalizeCountryCode(rawCountry) || '—';
    const iso2 = COUNTRY_ISO3_TO_ISO2[label] || (/^[A-Z]{2}$/.test(label) ? label : '');
    const flag = iso2 ? countryCodeToFlag(iso2) : '';
    const countryName = iso2 && COUNTRY_DISPLAY_NAMES ? COUNTRY_DISPLAY_NAMES.of(iso2) : '';

    return {
        label,
        flag,
        tooltip: countryName ? `${countryName} (${label})` : label,
    };
}

// Plain spans (no MUI), so table cells and chip labels can both hold it.
export function CountryChipLabel({ display }) {
    return (
        <span className="inline-flex items-center gap-1">
            {display?.flag && (
                <span aria-hidden="true" className="leading-none">
                    {display.flag}
                </span>
            )}
            <span>{display?.label || '—'}</span>
        </span>
    );
}

CountryChipLabel.propTypes = {
    display: PropTypes.shape({
        label: PropTypes.string,
        flag: PropTypes.string,
    }),
};

export function renderCountryChipLabel(display) {
    return <CountryChipLabel display={display} />;
}

export function CountryChip({
    country,
    size = 'small',
    variant = 'outlined',
    color = 'default',
    sx = null,
}) {
    const display = getCountryChipDisplay(country);
    const chip = (
        <Chip
            size={size}
            variant={variant}
            color={color}
            label={renderCountryChipLabel(display)}
            aria-label={display.tooltip}
            sx={sx}
        />
    );
    return (
        <Tooltip title={display.tooltip} describeChild arrow>
            {chip}
        </Tooltip>
    );
}

CountryChip.propTypes = {
    country: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    size: PropTypes.string,
    variant: PropTypes.string,
    color: PropTypes.string,
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
};

// The chip's flag and code as plain table text (no MUI, so it can sit in a
// DataTable cell); hovering shows the country name. The tooltip closes as
// the pointer leaves the text, so it can't cover a neighbouring cell's click.
export function CountryFlagText({ country }) {
    const display = getCountryChipDisplay(country);
    return (
        <KitTooltip content={display.tooltip}>
            <span>{renderCountryChipLabel(display)}</span>
        </KitTooltip>
    );
}

CountryFlagText.propTypes = {
    country: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export const SECURITY_DISPLAY_STATUS_METADATA = {
    // Phase 4.5/4.6 Security Resolution state vocabulary.
    ready: {
        label: 'Ready',
        color: 'success',
        icon: CheckCircleIcon,
        tooltip: 'Ready.',
    },
    // Legacy estimates remain visible as unsupported; they are never ready data.
    imputed: {
        label: 'Factor data unavailable',
        color: 'warning',
        icon: WarningAmberIcon,
        tooltip: 'Verified factor data is unavailable. Estimated data is not supported; the security remains excluded.',
    },
    not_evaluated: {
        label: 'Not evaluated',
        color: 'text',
        icon: RemoveIcon,
        tooltip: 'Not evaluated.',
    },
    resolved: {
        label: 'Resolved',
        color: 'success',
        icon: CheckCircleIcon,
        tooltip: 'Resolved',
    },
    included: {
        label: 'Resolved',
        color: 'success',
        icon: CheckCircleIcon,
        tooltip: 'Resolved',
    },
    resolved_with_notes: {
        label: 'Resolved with notes',
        color: 'success',
        icon: CheckCircleOutlineIcon,
        hasNoteMarker: true,
        tooltip: 'Resolved; review the note for context.',
    },
    pending: {
        label: 'Pending',
        color: 'info',
        icon: PendingIcon,
        tooltip: 'Waiting for market data registration.',
    },
    waiting: {
        label: 'Pending',
        color: 'info',
        icon: PendingIcon,
        tooltip: 'Waiting for market data registration.',
    },
    pending_market_data: {
        label: 'Pending market data',
        color: 'info',
        icon: PendingIcon,
        tooltip: 'Pending market data registration.',
    },
    needs_review: {
        label: 'Needs Review',
        color: 'error',
        icon: ErrorIcon,
        tooltip: 'Needs Review',
    },
    could_not_resolve: {
        label: 'Needs Review',
        color: 'error',
        icon: ErrorIcon,
        tooltip: 'Needs Review',
    },
    needs_attention: {
        label: 'Needs attention',
        color: 'error',
        icon: ErrorIcon,
        tooltip: 'Needs attention.',
    },
    excluded: {
        label: 'Excluded',
        color: 'text',
        icon: BlockIcon,
        tooltip: 'Excluded from this revision.',
    },
    removed: {
        label: 'Removed',
        color: 'error',
        icon: DeleteOutlineIcon,
        tooltip: 'Removed in this revision.',
    },
    not_completed_before_replaced: {
        label: 'Needs attention',
        color: 'warning',
        icon: ErrorIcon,
        tooltip: 'Market data was still pending when a newer watchlist version was created.',
    },
    unknown: {
        label: 'Status unavailable',
        color: 'warning',
        icon: InfoIcon,
        tooltip: 'Status unavailable.',
    },
};

export function getSecurityStatusPresentation(statusLike, overrides = {}) {
    const key = String(
        statusLike?.code
        || statusLike?.bucket
        || statusLike?.status
        || statusLike
        || 'unknown',
    ).trim();
    const metadata = SECURITY_DISPLAY_STATUS_METADATA[key] || SECURITY_DISPLAY_STATUS_METADATA.unknown;
    const statusLabel = key === 'included' ? metadata.label : statusLike?.label;
    const statusTooltip = key === 'included' ? metadata.tooltip : (statusLike?.tooltip || statusLike?.message);
    return {
        ...metadata,
        ...overrides,
        key,
        label: overrides.label || statusLabel || metadata.label,
        tooltip: overrides.tooltip || statusTooltip || metadata.tooltip,
    };
}

export function SecurityStatusIcon({
    presentation,
    status,
    size = 28,
    iconSize = 19,
    tooltip = '',
    label = '',
}) {
    const display = presentation || getSecurityStatusPresentation(status, { tooltip, label });
    const StatusIcon = display.icon || InfoIcon;
    const ariaLabel = label || display.label;
    const tooltipText = tooltip || display.tooltip || ariaLabel;
    return (
        <Tooltip title={tooltipText} arrow>
            <Box
                component="span"
                role="img"
                aria-label={ariaLabel}
                sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: size,
                    height: Math.max(22, size - 4),
                    color: (theme) => (
                        display.color === 'text'
                            ? theme.palette.text.secondary
                            : theme.palette[display.color]?.main || theme.palette.text.secondary
                    ),
                }}
            >
                <StatusIcon sx={{ fontSize: iconSize }} />
                {display.hasNoteMarker && (
                    <InfoIcon
                        aria-hidden="true"
                        sx={{
                            position: 'absolute',
                            right: 2,
                            bottom: 1,
                            fontSize: Math.max(9, Math.floor(iconSize * 0.55)),
                            borderRadius: '50%',
                            backgroundColor: 'background.paper',
                        }}
                    />
                )}
            </Box>
        </Tooltip>
    );
}

SecurityStatusIcon.propTypes = {
    presentation: PropTypes.shape({
        label: PropTypes.string,
        color: PropTypes.string,
        icon: PropTypes.elementType,
        tooltip: PropTypes.string,
        hasNoteMarker: PropTypes.bool,
    }),
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    size: PropTypes.number,
    iconSize: PropTypes.number,
    tooltip: PropTypes.string,
    label: PropTypes.string,
};
